import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import {
  MODAL_CANVA_IMAGE_CONFIG_TYPE_LABEL,
  MODAL_CANVA_IMAGE_CONFIG_WIDTH_LABEL,
  MODAL_CANVA_IMAGE_CONFIG_HEIGHT_LABEL,
  MODAL_CANVA_IMAGE_CONFIG_CANCEL_BUTTON,
  MODAL_CANVA_ACCOUNT_LABEL,
  ERROR_WIDTH_RANGE,
  ERROR_HEIGHT_RANGE,
  MODAL_CANVA_IMAGE_CONFIG_CONTENT_TYPE_LABEL,
  FIELD_REQUIRED,
  IMAGE,
  VIDEO,
} from "../../i18n/keysTranslations";
import {
  imageTypes,
  presetDimensions,
  IMAGE_TYPE_SQUARE,
  IMAGE_TYPE_LANDSCAPE,
  IMAGE_TYPE_PORTRAIT,
  MODAL_CANVA_IMAGE_CONFIG_MODE_CONFIG,
  MODAL_CANVA_IMAGE_CONFIG_MODE_CREATE,
  MODAL_CANVA_IMAGE_CONFIG_MODE_EDIT,
  MODAL_CANVA_IMAGE_CONFIG_MODE_DISCONNECT,
} from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import ModalBasicLayout from "./ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { Typography } from "@mui/material";
import Select from "../Form/Select";
import TextField from "../Form/TextField";
import SelectCanvaAccountWithConnect from "../Canva/SelectCanvaAccountWithConnect";

const ModalCanvaImageConfig = ({
  modalOpen,
  onCloseModal = () => {},
  onConfirm = () => {},
  mode = MODAL_CANVA_IMAGE_CONFIG_MODE_CREATE,
}) => {
  const { t } = useTranslationApp();

  const isEditMode = mode === MODAL_CANVA_IMAGE_CONFIG_MODE_EDIT;
  const isDisconnectMode = mode === MODAL_CANVA_IMAGE_CONFIG_MODE_DISCONNECT;
  const showOnlyAccountField = isEditMode || isDisconnectMode;

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      canvaAccount: null,
      contentType: "image",
      imageType: IMAGE_TYPE_SQUARE,
      width: 1080,
      height: 1080,
    },
  });

  const contentType = watch("contentType");

  const watchedValues = watch();

  const translatedImageTypes = imageTypes
    .map((type) => ({
      ...type,
      label: t(type.label),
    }))
    .filter((type) => {
      if (contentType === "video" && type.value === IMAGE_TYPE_SQUARE)
        return false;
      return true;
    });

  const contentTypeOptions = [
    { value: "image", label: t(IMAGE) },
    { value: "video", label: t(VIDEO) },
  ];

  const onChangeImageType = (newType) => {
    const dimensions = presetDimensions[newType];
    if (dimensions) {
      setValue("width", dimensions.width);
      setValue("height", dimensions.height);
    }
  };

  const getOrientation = (width, height) => {
    if (width === height) return IMAGE_TYPE_SQUARE;
    if (width > height) return IMAGE_TYPE_LANDSCAPE;
    return IMAGE_TYPE_PORTRAIT;
  };

  const onConfirmModalCanvaImageConfig = () => {
    const { canvaAccount, contentType, width, height } = watchedValues;
    const integrationID = canvaAccount?.id || canvaAccount?.integrationID;

    if (isEditMode || isDisconnectMode) {
      onConfirm({ integrationID });
      return;
    }

    const orientation = getOrientation(width, height);
    onConfirm({
      integrationID,
      contentType,
      width,
      height,
      orientation,
    });
  };

  const onCancelModalCanvaImageConfig = () => {
    onCloseModal();
  };

  const config = MODAL_CANVA_IMAGE_CONFIG_MODE_CONFIG[mode];
  const title = t(config.title);
  const description = t(config.description);
  const primaryButtonText = config.primaryButtonText;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCancelModalCanvaImageConfig}
      ariaLabel={title}
      title={title}
      description={description}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t(MODAL_CANVA_ACCOUNT_LABEL)}
          </Typography>
          <SelectCanvaAccountWithConnect
            control={control}
            name="canvaAccount"
            fullWidth
            variant="filled"
            errors={errors}
          />
        </Box>

        {!showOnlyAccountField && (
          <>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(MODAL_CANVA_IMAGE_CONFIG_CONTENT_TYPE_LABEL)}
              </Typography>
              <Select
                control={control}
                name="contentType"
                options={contentTypeOptions}
                actionsChange={(value) => {
                  if (value === "video") {
                    onChangeImageType(IMAGE_TYPE_PORTRAIT);
                    setValue("imageType", IMAGE_TYPE_PORTRAIT);
                  }
                }}
                fullWidth
                variant="filled"
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
                errors={errors}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(MODAL_CANVA_IMAGE_CONFIG_TYPE_LABEL)}
              </Typography>
              <Select
                control={control}
                name="imageType"
                options={translatedImageTypes}
                fullWidth
                variant="filled"
                actionsChange={onChangeImageType}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
                errors={errors}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t(MODAL_CANVA_IMAGE_CONFIG_WIDTH_LABEL)}
                </Typography>
                <TextField
                  control={control}
                  name="width"
                  type="number"
                  fullWidth
                  variant="filled"
                  rules={{
                    required: {
                      value: true,
                      message: t(ERROR_WIDTH_RANGE),
                    },
                    min: {
                      value: 100,
                      message: t(ERROR_WIDTH_RANGE),
                    },
                    max: {
                      value: 5000,
                      message: t(ERROR_WIDTH_RANGE),
                    },
                  }}
                  errors={errors}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t(MODAL_CANVA_IMAGE_CONFIG_HEIGHT_LABEL)}
                </Typography>
                <TextField
                  control={control}
                  name="height"
                  type="number"
                  fullWidth
                  variant="filled"
                  rules={{
                    required: {
                      value: true,
                      message: t(ERROR_HEIGHT_RANGE),
                    },
                    min: {
                      value: 100,
                      message: t(ERROR_HEIGHT_RANGE),
                    },
                    max: {
                      value: 5000,
                      message: t(ERROR_HEIGHT_RANGE),
                    },
                  }}
                  errors={errors}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
      <ContainerModalActions
        hasPadding={false}
        propsPrimaryButton={{
          showButton: true,
          text: primaryButtonText,
          onClick: onConfirmModalCanvaImageConfig,
        }}
        propsSecondaryButton={{
          showButton: true,
          text: MODAL_CANVA_IMAGE_CONFIG_CANCEL_BUTTON,
          onClick: onCancelModalCanvaImageConfig,
        }}
      />
    </ModalBasicLayout>
  );
};

export default ModalCanvaImageConfig;
